/**
 * Calendar Header Component
 *
 * Simplified header for calendar page:
 * - Calendar selector slot
 * - Action buttons (Schedule Post, optional Create Calendar)
 * - Settings dropdown
 *
 * Navigation and view controls moved to CalendarGrid
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Tooltip, CreatePostSplitButton } from '@/components/ui';
import { ICONS } from '@/constants/icons';
import { SettingsDropdown } from './SettingsDropdown';
import { BulkImportDialog } from '@/components/bulk/BulkImportDialog';
import { CanvaImportDialog } from '@/components/canva';

interface CalendarHeaderProps {
  onCreateCalendar?: () => void;
  onSchedulePost?: () => void;
  onDownloadCalendar?: () => void;
  onResetCalendar?: () => void;
  onManageHolidays?: () => void;
  onImportICS?: () => void;
  onManageMappingRules?: () => void;
  hasCalendars?: boolean;
  children?: React.ReactNode;
}

export const CalendarHeader = ({
  onCreateCalendar,
  onSchedulePost,
  onDownloadCalendar,
  onResetCalendar,
  onManageHolidays,
  onImportICS,
  onManageMappingRules,
  hasCalendars,
  children,
}: CalendarHeaderProps) => {
  const { t } = useTranslation('common');
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [canvaImportOpen, setCanvaImportOpen] = useState(false);

  const handleSchedulePost = () => {
    onSchedulePost?.();
  };

  return (
    <header className="bg-white px-4 sm:px-6 py-2 sticky top-navbar-height z-30">
      <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-4">
        {/* Left: Calendar Selector + New Calendar */}
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
          {/* Calendar Selector (passed as children) */}
          {children && <div className="min-w-0 flex-1 sm:max-w-[250px]">{children}</div>}

          {/* New Calendar Button (icon only with tooltip) */}
          {onCreateCalendar && (
            <Tooltip content={t('new_calendar')}>
              <button
                onClick={onCreateCalendar}
                className="hidden sm:flex w-8 h-8 items-center justify-center hover:bg-gray-100 rounded-lg transition-colors text-gray-600 hover:text-gray-800 flex-shrink-0"
                aria-label={t('new_calendar')}
              >
                <Icon name={ICONS.PLUS} size="sm" ariaHidden />
              </button>
            </Tooltip>
          )}
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {onSchedulePost && (
            <CreatePostSplitButton
              onCreatePost={handleSchedulePost}
              onBulkImport={() => setBulkImportOpen(true)}
              onCanvaImport={() => setCanvaImportOpen(true)}
              size="sm"
            />
          )}

          {/* Settings Dropdown — desktop only (on mobile it's in the sidebar) */}
          {hasCalendars && (
            <div className="hidden lg:block">
              <SettingsDropdown
                onDownload={onDownloadCalendar}
                onReset={onResetCalendar}
                onManageHolidays={onManageHolidays}
                onImportICS={onImportICS}
                onManageMappingRules={onManageMappingRules}
              />
            </div>
          )}
        </div>
      </div>

      {/* Bulk Import Dialog */}
      <BulkImportDialog
        isOpen={bulkImportOpen}
        onClose={() => setBulkImportOpen(false)}
        onSuccess={() => {
          setBulkImportOpen(false);
        }}
      />

      {/* Canva Import Dialog */}
      <CanvaImportDialog
        isOpen={canvaImportOpen}
        onClose={() => setCanvaImportOpen(false)}
        onSuccess={() => {
          setCanvaImportOpen(false);
        }}
      />
    </header>
  );
};
